import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Globe, Send } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\n\n${form.message}`
    );
    window.location.href = `mailto:info@fulfillflow.io?subject=${encodeURIComponent(
      "FulfillFlow Demo Request — " + (form.company || form.name)
    )}&body=${body}`;
  };

  return (
    <div data-testid="contact-page">
      <section className="relative pt-36 md:pt-44 pb-24 lg:pb-32 lp-grid-lines">
        <div className="lp-orb w-[500px] h-[400px] bg-sky-300/50 top-10 -left-40" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-blue-600 font-bold mb-4">Contact</p>
            <h1 className="lp-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0b1530] leading-[1.05]">
              Let's talk about <span className="lp-gradient-text">your warehouse.</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-lg">
              Tell us about your operation and we'll set up a guided demo of FulfillFlow tailored to your 3PL.
            </p>
            <div className="mt-10 space-y-5">
              <a href="mailto:info@fulfillflow.io" className="flex items-center gap-4 text-slate-700 hover:text-blue-600 transition-colors" data-testid="contact-email-link">
                <span className="lp-icon-chip !h-11 !w-11"><Mail size={20} strokeWidth={1.5} className="text-blue-600" /></span>
                <span className="text-base font-medium">info@fulfillflow.io</span>
              </a>
              <div className="flex items-center gap-4 text-slate-700">
                <span className="lp-icon-chip !h-11 !w-11"><Globe size={20} strokeWidth={1.5} className="text-blue-600" /></span>
                <span className="text-base font-medium">fulfillflow.io</span>
              </div>
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            onSubmit={submit}
            className="lp-card rounded-2xl border border-slate-200 p-8 lg:p-10 space-y-5"
            data-testid="contact-form"
          >
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Your name</label>
              <input
                required value={form.name} onChange={set("name")}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-[#0b1530] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Jane Smith" data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Work email</label>
              <input
                required type="email" value={form.email} onChange={set("email")}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-[#0b1530] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="jane@yourcompany.com" data-testid="contact-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Company / 3PL name</label>
              <input
                value={form.company} onChange={set("company")}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-[#0b1530] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Acme Fulfillment" data-testid="contact-company-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Tell us about your operation</label>
              <textarea
                rows={4} value={form.message} onChange={set("message")}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-[#0b1530] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Warehouses, order volume, marketplaces you serve..." data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              className="w-full inline-flex justify-center items-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold px-8 py-4 transition-colors shadow-xl shadow-blue-600/30"
              data-testid="contact-submit-btn"
            >
              <Send size={17} strokeWidth={2} />
              Send Demo Request
            </button>
            <p className="text-xs text-slate-400 text-center">Opens your email client with the details pre-filled.</p>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
